import { useEffect, useState } from "react";
import { axiosGet } from "../apis/useAxios";

// Password rules of the airline (auth/password-policy = WebUserPasswordPolicy). The API checks them again on save; here they
// only show, while typing, which rules the new password already meets. Same rules as PasswordPolicyRules in the API.

/** Used until the policy is loaded (or when it cannot be): the dev / live policy of 2026. */
export const DEFAULT_POLICY = {
  minLength: 8,
  maxLength: 50,
  minUpperCaseLetter: 1,
  minLowerCaseLetter: 1,
  minNumber: 1,
  minSpecialCharacter: 1,
};

const count = (text, test) => [...text].filter(test).length;
const isUpper = (c) => c !== c.toLowerCase();
const isLower = (c) => c !== c.toUpperCase();
const isDigit = (c) => c >= "0" && c <= "9";
/** Anything that is not a letter, a number or a space. */
const isSpecial = (c) => !isUpper(c) && !isLower(c) && !isDigit(c) && c.trim() !== "";

/** The rules of the policy with whether the password meets each one: [{ key, text, met }]. Rules set to 0 are left out. */
export function passwordRuleChecks(policy, password) {
  const p = { ...DEFAULT_POLICY, ...(policy ?? {}) };
  const text = password ?? "";
  const min = Math.max(1, p.minLength || 0);
  const rules = [
    { key: "length", text: `At least ${min} characters`, met: text.length >= min && text.length <= (p.maxLength || 50) },
    { key: "upper", n: p.minUpperCaseLetter, text: "upper case letter", met: count(text, isUpper) >= p.minUpperCaseLetter },
    { key: "lower", n: p.minLowerCaseLetter, text: "lower case letter", met: count(text, isLower) >= p.minLowerCaseLetter },
    { key: "number", n: p.minNumber, text: "number", met: count(text, isDigit) >= p.minNumber },
    { key: "special", n: p.minSpecialCharacter, text: "special character (e.g. @ # $ !)", met: count(text, isSpecial) >= p.minSpecialCharacter },
    { key: "spaces", text: "No space at the start or end", met: text.length > 0 && text === text.trim() },
  ];
  return rules
    .filter((r) => r.n === undefined || r.n > 0)
    .map(({ key, n, text: t, met }) => ({ key, text: n === undefined ? t : `At least ${n} ${t}`, met }));
}

/** Whether the password meets every rule. */
export const meetsPolicy = (policy, password) => passwordRuleChecks(policy, password).every((r) => r.met);

/** Loads the policy once; DEFAULT_POLICY until then. */
export function usePasswordPolicy() {
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  useEffect(() => {
    let alive = true;
    axiosGet("auth/password-policy")
      .then((r) => {
        if (alive && r?.isSuccess && r.payload) setPolicy({ ...DEFAULT_POLICY, ...r.payload, maxLength: r.payload.maxLength || 50 });
      })
      .catch(() => {}); // the API checks the rules on save anyway
    return () => {
      alive = false;
    };
  }, []);
  return policy;
}
